import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../hooks/AuthProvider";
import Spinner from "../Components/Spinner";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteDataIcon from "@mui/icons-material/CancelOutlined";
import TranferIcon from "@mui/icons-material/SyncAltOutlined";
import PingIcon from "@mui/icons-material/RefreshOutlined";
import RebootIcon from "@mui/icons-material/CachedOutlined";
import RenameIcon from "@mui/icons-material/KeyboardOutlined";
import EditIcon from "@mui/icons-material/Edit";
import AlarmOnOutlinedIcon from "@mui/icons-material/AlarmOnOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { STATIC_ICONS } from "../Constants";
import devicesApi from "../api/devicesApi";
import "../widgets/widget.css";
import { useNavigate } from "react-router-dom";
import { AddDevice } from "./AddDevice";
import { RenameDevice, EditDeviceId, TransferDevice } from "./DeviceDetails";
import {
  deleteDevice,
  deleteMultiDevices,
  deleteDeviceData,
  deviceAction,
} from "../lib/actions";

import moment from "moment";
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import { DeviceIcon } from "../Icons";

import { LanguageContext } from "../context/LanguageContext";
import devTranslations from "../languages/device.json";

import { getErrorMessage } from "../utils/errorUtils";
import AppDialog from "../Components/AppDialog";

import DevicesMapModal from "./DevicesMapModal";

const DEVICE_INTERNAL_FIELDS = {
  rowId: "_row_id",
  did: "_did",
  ownerId: "_owner_id",
  planStatus: "_plan_status",
  durationPlan: "_duration_plan",
};

const getDeviceRowId = (row) => row?.[DEVICE_INTERNAL_FIELDS.rowId] ?? row?.id;
const getDeviceDid = (row) => row?.[DEVICE_INTERNAL_FIELDS.did] ?? row?.did;
const getDeviceOwnerId = (row) => row?.[DEVICE_INTERNAL_FIELDS.ownerId] ?? row?.owner_id;
const getDevicePlanStatus = (row) => row?.[DEVICE_INTERNAL_FIELDS.planStatus] ?? row?.plan_status;
const getDeviceDurationPlan = (row) => row?.[DEVICE_INTERNAL_FIELDS.durationPlan] ?? row?.duration_plan;

const resolveDeviceTemplateImageUrl = (value) => {
  const normalized = String(value || "").trim().replace(/\\/g, "/");
  if (!normalized) return "";
  if (/^(?:blob:|data:|https?:\/\/)/i.test(normalized)) return normalized;

  const relativePath = normalized
    .replace(/^\/+/, "")
    .replace(/^(?:static\/files\/)?icons\//i, "");
  return `${STATIC_ICONS}${relativePath}`;
};

const DeviceTemplateImage = ({ value }) => {
  const [failed, setFailed] = useState(false);
  const src = resolveDeviceTemplateImageUrl(value);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <DeviceIcon color={"var(--accent)"} />;
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      style={{
        width: "36px",
        height: "36px",
        objectFit: "contain",
        display: "block",
        margin: "6px auto 0",
      }}
    />
  );
};

export const Devices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [addDevice, setAddDevice] = useState(false);
  const [rename, setRename] = useState({ open: false, id: 0 });
  const [transfer, setTransfer] = useState({ open: false, id: 0 });
  const [editDid, setEditDid] = useState({ open: false, id: 0, row: null });
  const [searchTerm] = useState("");
  const [deviceTableMeta, setDeviceTableMeta] = useState(null);

  const navigate = useNavigate();

  const { lang } = useContext(LanguageContext);
  const t = devTranslations[lang];
  const isRtl = ["ar", "ur"].includes(lang);

  const { permissions, org, user } = useAuth();
  const pagingoptions = [10, 20, 50, 100];
  const canEdit = permissions["edit_devices"];
  const canViewDevices = Boolean(permissions["view_devices"]);
  const canViewMap = Boolean(permissions["devices_map"]);
  const isSuperUser = String(user?.role || "").toLowerCase() === "super";
  const isAdminLikeUser =
    String(user?.role || "").toLowerCase() === "super" ||
    String(user?.role || "").toLowerCase() === "owner" ||
    String(user?.role || "").toLowerCase() === "admin";
  const [view, setView] = useState(canViewDevices ? "table" : "map");
  const visibleDeviceFields = useMemo(() => {
    const apiVisibleFields = deviceTableMeta?.devicesTable?.visibleFields;
    return Array.isArray(apiVisibleFields) ? apiVisibleFields : [];
  }, [deviceTableMeta]);
  const visibleDeviceFieldSet = useMemo(() => new Set(visibleDeviceFields), [visibleDeviceFields]);
  const defaultSortField = visibleDeviceFieldSet.has("name")
    ? "name"
    : visibleDeviceFields[0] || "status";

  // ---------------------------
  // AppDialog "alert" function
  // ---------------------------
  const resolverRef = useRef(null);
  const refreshDevicesRef = useRef(() => {});
  const [dialog, setDialog] = useState({
    open: false,
    type: "alert", // "alert" | "confirm"
    variant: "info", // "info" | "danger" | "error"
    icon: "alert", // "alert" | "delete"
    title: "",
    message: "",
    confirmText: "Ok",
    cancelText: "Cancel",
    showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: opts?.type || "alert",
          variant: opts?.variant || "info",
          icon: opts?.icon || "alert",
          title: opts?.title || "",
          message: opts?.message || "",
          confirmText: opts?.confirmText || "Ok",
          cancelText: opts?.cancelText || "Cancel",
          showCancel: Boolean(opts?.showCancel),
        });
      }),
    []
  );

  const showAlert = useCallback(
    async (type, message) => {
      const variant =
        type === "error" ? "error" : type === "warning" ? "info" : "info";

      await alert({
        type: "alert",
        variant,
        icon: "alert",
        title:
          type === "error"
            ? (t?.alert?.errorTitle || "Error")
            : type === "warning"
              ? (t?.alert?.warningTitle || "Warning")
              : (t?.alert?.infoTitle || "Info"),
        message: message || "",
        confirmText: "Ok",
      });
    },
    [alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.infoTitle]
  );

  const askDurationDays = useCallback(async (defaultValue = "") => {
    const input = window.prompt(
      t?.planFlow?.durationPrompt || "Enter duration in days",
      String(defaultValue || "")
    );
    if (input === null) return null;
    const n = Number(input);
    if (!Number.isInteger(n) || n <= 0) {
      await showAlert("error", t?.planFlow?.durationInvalid || "Duration must be a positive integer.");
      return undefined;
    }
    return n;
  }, [showAlert, t?.planFlow?.durationInvalid, t?.planFlow?.durationPrompt]);

  const superPlanAction = useCallback(async (row, action) => {
    try {
      const payload = { action };
      if (["activate", "renew", "change_plan"].includes(action)) {
        const days = await askDurationDays(getDeviceDurationPlan(row) || "");
        if (days === null) return;
        if (days === undefined) return;
        payload.duration_plan = days;
      }

      const res = await devicesApi.planAction(getDeviceRowId(row), payload);
      await showAlert("info", res?.data?.message || "Plan action applied.");
      refreshDevicesRef.current();
    } catch (err) {
      await showAlert("error", getErrorMessage(err));
    }
  }, [askDurationDays, showAlert]);

  const ownerPlanRequest = useCallback(async (row, action) => {
    try {
      const payload = { action };
      if (action !== "disable") {
        const days = await askDurationDays(getDeviceDurationPlan(row) || "");
        if (days === null) return;
        if (days === undefined) return;
        payload.duration_plan = days;
      }
      const res = await devicesApi.planRequest(getDeviceRowId(row), payload);
      await showAlert("info", res?.data?.message || "Request submitted.");
      refreshDevicesRef.current();
    } catch (err) {
      await showAlert("error", getErrorMessage(err));
    }
  }, [askDurationDays, showAlert]);

  const nameColumnWidth = useMemo(() => {
    const minWidth = 200;
    const maxWidth = 720;
    const headerName = String(t?.column?.name || "Name");
    const longestName = rows.reduce((longest, row) => {
      const current = String(row?.name || "");
      return current.length > longest.length ? current : longest;
    }, headerName);

    const estimatedWidth = Math.ceil((longestName.length * 9) + 56);
    return Math.max(minWidth, Math.min(maxWidth, estimatedWidth));
  }, [rows, t?.column?.name]);

  const baseColumns = useMemo(
    () => ({
      icon: {
        field: "icon",
        headerName: "",
        width: 60,
        align: "center",
        renderCell: (params) => <DeviceTemplateImage value={params.row.icon} />,
      },
      name: {
        field: "name",
        headerName: t.column.name,
        width: nameColumnWidth,
        align: "left",
        headerAlign: "left",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      status: {
        field: "status",
        headerName: t.column.status,
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            className="details-head"
            style={{
              alignItems: "center",
              fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            <div className={`details-status ${params.row.status === "Online" ? "active" : null}`}></div>
            {params.row.status}
          </div>
        ),
        headerClassName: "font-responsive",
      },
      owner_name: {
        field: "owner_name",
        headerName: t.column.ownerName,
        width: 140,
        align: "left",
        headerAlign: "left",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      main_organization_name: {
        field: "main_organization_name",
        headerName: t.column.mainOrganizationName || "Main Organization Name",
        width: 220,
        align: "left",
        headerAlign: "left",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      sub_organization_name: {
        field: "sub_organization_name",
        headerName: t.column.subOrganizationName || "Sub Organization Name",
        width: 220,
        align: "left",
        headerAlign: "left",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      did: {
        field: "did",
        headerName: t.column.did,
        width: 150,
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      tid: {
        field: "tid",
        headerName: t.column.tid,
        width: 150,
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      address: {
        field: "address",
        headerName: t.column.address || "Address",
        width: 260,
        align: "left",
        headerAlign: "left",
        valueGetter: (_value, row) => row?.address || row?.location_address || "",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      latitude: {
        field: "latitude",
        headerName: t.column.latitude || "Latitude",
        width: 140,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) => (value === null || value === undefined || value === "" ? "-" : value),
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      longitude: {
        field: "longitude",
        headerName: t.column.longitude || "Longitude",
        width: 140,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) => (value === null || value === undefined || value === "" ? "-" : value),
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      template_name: {
        field: "template_name",
        headerName: t.column.templateName,
        width: 200,
        align: "left",
        headerAlign: "left",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      created: {
        field: "created",
        headerName: t.column.created,
        width: 200,
        align: "center",
        headerAlign: "center",
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        valueFormatter: (params) => moment(params).format("DD/MM/YYYY HH:mm:ss"),
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      last_reported: {
        field: "last_reported",
        headerName: t.column.reported,
        width: 200,
        align: "center",
        headerAlign: "center",
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        valueFormatter: (params) => moment(params).format("DD/MM/YYYY HH:mm:ss"),
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      firmware: {
        field: "firmware",
        headerName: t.column.firmware,
        width: 100,
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      board: {
        field: "board",
        headerName: t.column.board,
        width: 100,
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      build: {
        field: "build",
        headerName: t.column.build,
        width: 200,
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      firmware_updated_on: {
        field: "firmware_updated_on",
        headerName: t.column.firmwareUpdatedOn,
        width: 200,
        align: "center",
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        valueFormatter: (params) => moment(params).format("DD/MM/YYYY HH:mm:ss"),
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      expired_at: {
        field: "expired_at",
        headerName: t.column.expired_at || "Expired At",
        width: 200,
        align: "center",
        headerAlign: "center",
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        valueFormatter: (params) => (params ? moment(params).format("DD/MM/YYYY HH:mm:ss") : ""),
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      plan_status: {
        field: "plan_status",
        headerName: t.column.plan_status || "Plan Status",
        width: 170,
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      duration_plan: {
        field: "duration_plan",
        headerName: t.column.duration_plan || "Duration (days)",
        width: 170,
        type: "number",
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
      remaining_time_days: {
        field: "remaining_time_days",
        headerName: t.column.remainingTimeDays || "Remaining Time (Day)",
        width: 190,
        type: "number",
        align: "center",
        headerAlign: "center",
        headerClassName: "font-responsive",
        cellClassName: "font-responsive",
      },
    }),
    [nameColumnWidth, t]
  );

  const canRename = permissions["rename_devices"];
  const canTransfer = permissions["transfer_devices"];
  const canControl = permissions["control_devices"];
  const canReboot = permissions["reboot_devices"];
  const canDeleteData = permissions["delete_device_data"];
  const canDeleteDevice = Boolean(permissions["delete_devices"]);
  const canShowPlanActions = isSuperUser || !isAdminLikeUser;

  const canShowActionsColumn =
    canEdit || canRename || canTransfer || canControl || canReboot || canDeleteData || canDeleteDevice || canShowPlanActions;

  const columns = useMemo(() => {
    const orderedVisibleColumns = visibleDeviceFields
      .map((field) => baseColumns[field])
      .filter(Boolean);

    if (!canShowActionsColumn) return orderedVisibleColumns;
    return [
      {
        field: "actions",
        headerName: t.column.actions,
        type: "actions",
        width: 100,
        getActions: (params) =>
          [
            canRename ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.tooltipRename}>
                    <RenameIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t.tooltipRename}
                onClick={() => setRename({ open: true, id: getDeviceRowId(params.row) })}
              />
            ) : null,
            canEdit ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t?.deviceDetails?.editDevice || t.tooltipEditDid}>
                    <EditIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t?.deviceDetails?.editDevice || t.tooltipEditDid}
                onClick={() =>
                  setEditDid({
                    open: true,
                    id: getDeviceRowId(params.row),
                    row: params.row,
                  })
                }
              />
            ) : null,
            canTransfer ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.tooltipTransfer}>
                    <TranferIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t.tooltipTransfer}
                onClick={() => setTransfer({ open: true, id: getDeviceRowId(params.row) })}
              />
            ) : null,
            canReboot ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.tooltipReboot}>
                    <RebootIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t.tooltipReboot}
                onClick={() => deviceAction(getDeviceDid(params.row), "reboot", null, alert)}
              />
            ) : null,
            canControl ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.tooltipPing}>
                    <PingIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t.tooltipPing}
                onClick={() => deviceAction(getDeviceDid(params.row), "ping", null, alert)}
              />
            ) : null,
            canDeleteData ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.tooltipDeleteData}>
                    <DeleteDataIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t.tooltipDeleteData}
                onClick={() =>
                  deleteDeviceData(getDeviceDid(params.row), alert, () => refreshDevicesRef.current())
                }
              />
            ) : null,
            canDeleteDevice ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.tooltipDelete}>
                    <DeleteIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t.tooltipDelete}
                onClick={() => deleteDevice(getDeviceRowId(params.row), () => refreshDevicesRef.current(), alert)}
              />
            ) : null,
            isSuperUser ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t?.planFlow?.activate || "Activate / Renew Plan"}>
                    <AlarmOnOutlinedIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t?.planFlow?.activate || "Activate / Renew Plan"}
                onClick={() =>
                  superPlanAction(
                    params.row,
                    getDevicePlanStatus(params.row) === "expired" ? "renew" : "activate"
                  )
                }
              />
            ) : null,
            isSuperUser ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t?.planFlow?.disable || "Disable Plan"}>
                    <BlockOutlinedIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t?.planFlow?.disable || "Disable Plan"}
                onClick={() => superPlanAction(params.row, "disable")}
              />
            ) : null,
            !isAdminLikeUser &&
            Number(getDeviceOwnerId(params.row)) === Number(user?.id) &&
            getDevicePlanStatus(params.row) === "expired" ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t?.planFlow?.requestRenew || "Request Renew"}>
                    <SendOutlinedIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t?.planFlow?.requestRenew || "Request Renew"}
                onClick={() => ownerPlanRequest(params.row, "renew_request")}
              />
            ) : null,
            !isAdminLikeUser && Number(getDeviceOwnerId(params.row)) === Number(user?.id) ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t?.planFlow?.requestNewPlan || "Request New Plan"}>
                    <SendOutlinedIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t?.planFlow?.requestNewPlan || "Request New Plan"}
                onClick={() => ownerPlanRequest(params.row, "new_plan_request")}
              />
            ) : null,
            !isAdminLikeUser && Number(getDeviceOwnerId(params.row)) === Number(user?.id) ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t?.planFlow?.disable || "Disable Plan"}>
                    <BlockOutlinedIcon
                      style={{
                        fontSize:
                          "calc(var(--body-font-size) * var(--font-size-multiplier))",
                      }}
                    />
                  </Tooltip>
                }
                showInMenu
                label={t?.planFlow?.disable || "Disable Plan"}
                onClick={() => ownerPlanRequest(params.row, "disable")}
              />
            ) : null,
          ].filter(Boolean),
      },
      ...orderedVisibleColumns,
    ];
  }, [
    canShowActionsColumn,
    baseColumns,
    visibleDeviceFields,
    t,
    canEdit,
    canRename,
    canTransfer,
    canControl,
    canReboot,
    canDeleteData,
    canDeleteDevice,
    isSuperUser,
    isAdminLikeUser,
    ownerPlanRequest,
    superPlanAction,
    user?.id,
    alert,
  ]);

  const streamRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const snapshotLoadingRef = useRef(false);
  const hasReceivedDevicePayloadRef = useRef(false);

  const fetchDevicesSnapshot = useCallback(async () => {
    if (!org?.id) {
      hasReceivedDevicePayloadRef.current = false;
      setRows([]);
      setDeviceTableMeta(null);
      setRowCount(0);
      setLoading(false);
      return false;
    }

    if (snapshotLoadingRef.current) {
      return false;
    }

    snapshotLoadingRef.current = true;

    try {
      const activeSort =
        sortModel && sortModel.length > 0
          ? sortModel[0]
          : { field: defaultSortField, sort: "asc" };
      const queryParams =
        view === "map"
          ? {}
          : {
              page,
              pageSize,
              sortField: activeSort.field || defaultSortField,
              sortOrder: activeSort.sort || "asc",
              filterModel: JSON.stringify(filterModel || { items: [] }),
            };
      const res = await devicesApi.filtered(org.id, queryParams);

      if (Array.isArray(res?.data?.data)) {
        hasReceivedDevicePayloadRef.current = true;
        setRows(res.data.data);
        setDeviceTableMeta(res?.data?.meta || null);
        setRowCount(res?.data?.pagination?.total ?? res.data.data.length);
        return true;
      }

      console.warn("[Devices] Ignoring unexpected snapshot payload:", res?.data);
      return false;
    } catch (err) {
      console.error("[Devices] Failed to fetch snapshot:", err);
      return false;
    } finally {
      snapshotLoadingRef.current = false;
      setLoading(false);
    }
  }, [defaultSortField, filterModel, org?.id, page, pageSize, sortModel, view]);

  const buildDeviceGridQueryParams = useCallback(() => {
    if (view === "map") {
      return {};
    }

    const activeSort =
      sortModel && sortModel.length > 0
        ? sortModel[0]
        : { field: defaultSortField, sort: "asc" };

    return {
      page,
      pageSize,
      sortField: activeSort.field || defaultSortField,
      sortOrder: activeSort.sort || "asc",
      filterModel: JSON.stringify(filterModel || { items: [] }),
    };
  }, [defaultSortField, filterModel, page, pageSize, sortModel, view]);

  const fetchDevices = useCallback(() => {
    if (!org?.id) {
      hasReceivedDevicePayloadRef.current = false;
      setRows([]);
      setDeviceTableMeta(null);
      setRowCount(0);
      setLoading(false);
      return;
    }

    hasReceivedDevicePayloadRef.current = false;
    setLoading(true);
    void fetchDevicesSnapshot();

    const connectStream = () => {
      try {
        if (streamRef.current) {
          streamRef.current.close();
          streamRef.current = null;
        }

        const evtSource = new EventSource(
          devicesApi.filteredSseUrl(org.id, buildDeviceGridQueryParams()),
          { withCredentials: true }
        );

        streamRef.current = evtSource;

        evtSource.onmessage = (event) => {
          try {
            const res = JSON.parse(event.data);

            if (res?.status === "ERROR") {
              console.warn("[Devices] Ignoring SSE error payload:", res);
              if (!hasReceivedDevicePayloadRef.current) {
                void fetchDevicesSnapshot();
              }
              return;
            }

            if (Array.isArray(res?.data)) {
              hasReceivedDevicePayloadRef.current = true;
              setRows(res.data);
              setDeviceTableMeta(res?.meta || null);
              setRowCount(res?.pagination?.total ?? res.data.length);
              setLoading(false);
              return;
            }

            console.warn("[Devices] Ignoring unexpected SSE payload:", res);
            if (!hasReceivedDevicePayloadRef.current) {
              void fetchDevicesSnapshot();
            } else {
              setLoading(false);
            }
          } catch (err) {
            console.error("Error parsing SSE data:", err);
            if (!hasReceivedDevicePayloadRef.current) {
              void fetchDevicesSnapshot();
            } else {
              setLoading(false);
            }
          }
        };

        evtSource.onerror = (err) => {
          console.error("Stream connection error:", err);

          evtSource.close();
          streamRef.current = null;

          if (!hasReceivedDevicePayloadRef.current) {
            void fetchDevicesSnapshot();
          }

          if (!retryTimeoutRef.current) {
            retryTimeoutRef.current = setTimeout(() => {
              retryTimeoutRef.current = null;
              connectStream();
            }, 3000);
          }
        };
      } catch (err) {
        console.error("Failed to initialize SSE:", err);
        setLoading(false);
      }
    };

    connectStream();
  }, [buildDeviceGridQueryParams, fetchDevicesSnapshot, org?.id]);

  useEffect(() => {
    refreshDevicesRef.current = fetchDevices;
  }, [fetchDevices]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        console.log("[SSE] cleanup – closing old stream");
        streamRef.current.close();
        streamRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      snapshotLoadingRef.current = false;
      hasReceivedDevicePayloadRef.current = false;
      setDeviceTableMeta(null);
    };
  }, [org?.id]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices, filterModel, page, pageSize, sortModel]);

  useEffect(() => {
    if (!canViewDevices && canViewMap && view !== "map") {
      setView("map");
      return;
    }

    if (!canViewMap && view === "map") {
      setView("table");
    }
  }, [canViewDevices, canViewMap, view]);

  const [selectedRows, setSelectedRows] = React.useState([]);

  const selectActions = canEdit
    ? [
      canDeleteDevice
        ? {
          title: "Delete",
          icon: (
            <DeleteIcon
              style={{
                color: "rgb(236, 69, 78)",
                marginInlineEnd: "20px",
                fontSize:
                  "calc(var(--body-font-size) * var(--font-size-multiplier))",
              }}
            />
          ),
          action: async () => {
            await deleteMultiDevices(selectedRows, fetchDevices, alert);
            setSelectedRows([]);
          },
        }
        : null,
    ].filter(Boolean)
    : [];

  const handleRowClick = (params) => {
    if (String(getDevicePlanStatus(params?.row) || "").toLowerCase() === "disabled") {
      showAlert(
        "warning",
        t?.planFlow?.disabledOpenBlocked || "This device is disabled. Device details are blocked."
      );
      return;
    }
    navigate("/devices/details/" + getDeviceRowId(params.row));
  };

  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    const value = searchTerm.toLowerCase();
    return (
      String(row.name || "").toLowerCase().includes(value) ||
      String(getDeviceRowId(row) || "").toLowerCase().includes(value) ||
      String(getDeviceDid(row) || "").toLowerCase().includes(value) ||
      String(row.address || row.location_address || "").toLowerCase().includes(value)
    );
  });

  // Map wants "all devices locations" (not filtered)
  const mapDevices = rows;
  const normalizedGridRows = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        id: getDeviceRowId(row),
      })),
    [filteredRows]
  );
  const effectiveDeviceSortModel =
    sortModel && sortModel.length > 0
      ? sortModel
      : [{ field: defaultSortField, sort: "asc" }];

  return canViewDevices || canViewMap ? (
    <div
      className="configuration devices-page"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      {rename.open ? (
        <RenameDevice state={rename} setState={setRename} refresh={fetchDevices} />
      ) : null}
      {transfer.open ? (
        <TransferDevice state={transfer} setState={setTransfer} refresh={fetchDevices} />
      ) : null}
      {addDevice ? (
        <AddDevice state={addDevice} setState={setAddDevice} refresh={fetchDevices} />
      ) : null}
      {editDid.open ? (
        <EditDeviceId state={editDid} setState={setEditDid} refresh={fetchDevices} />
      ) : null}

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 className="devices-title">{t.devicesPageTitle}</h2>

            <div className="devices-view-switch">
              {canViewDevices ? (
                <button
                  className={`devices-view-btn ${
                    view === "table" ? "active" : ""
                  }`}
                  onClick={() => setView("table")}
                >
                  Table
                </button>
              ) : null}

              {canViewMap ? (
                <button
                  className={`devices-view-btn ${
                    view === "map" ? "active" : ""
                  }`}
                  onClick={() => setView("map")}
                >
                  Map
                </button>
              ) : null}
            </div>
          </div>

          {permissions["provision_new_devices"] ? (
            <button className="devices-primary-btn" onClick={() => setAddDevice(true)}>
              <span>{t.newDevice}</span>
            </button>
          ) : null}
        </div>

        {view === "map" ? (
          <DevicesMapModal
            embedded
            open={true}
            onClose={() => setView("table")}
            devices={mapDevices}
            visibleFields={visibleDeviceFields}
            isRtl={isRtl}
            t={t}
            onOpenDevice={(deviceId) => navigate("/devices/details/" + deviceId)}
            alert={alert}
          />
        ) : loading && filteredRows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : filteredRows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className="devices-grid-wrapper">
            <DataGridTable
              columns={columns}
              rows={normalizedGridRows}
              idcolumn={"id"}
              pagingoptions={pagingoptions}
              sortField={defaultSortField}
              sortType="asc"
              handleRowClick={handleRowClick}
              checkboxSelection={canDeleteDevice}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey="devices"
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel: effectiveDeviceSortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
              getRowClassName={(params) =>
                String(getDevicePlanStatus(params?.row) || "").toLowerCase() === "disabled"
                  ? "disabled-device-row"
                  : ""
              }
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            {t.noData}
          </div>
        )}
      </div>
    </div>
  ) : null;
};
